import React, { useCallback, useMemo, useState } from 'react';
import { Select, Typography } from 'antd';
import PageTabs from '../page/PageTabs';
import AiCurateForm from './AiCurateForm';
import AiRunResults from './AiRunResults';
import ReviewPanel from './ReviewPanel';
import QuestionsPanel from './QuestionsPanel';
import PublishPanel from './PublishPanel';
import SourceDomainManager from './SourceDomainManager';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import { curatableStandardOptions } from '../../utils/questionBankCuratorModel';

const { Text } = Typography;

/** Everything that depends on the selected standard. Remounted (key) when the standard changes so no
 * run result or draft from one standard can ever render under another. */
const Workspace = ({ standardId, standardLabel }) => {
    const { request } = useApiRequest();
    const [rev, setRev] = useState(0);
    const [lastRun, setLastRun] = useState(null); // { result, drafts }
    const bump = useCallback(() => setRev((r) => r + 1), []);

    const preview = useResource(
        async () => (await request('GET', `/admin/question-bank/versions/${encodeURIComponent(standardId)}/publish-preview`)).data,
        [request, standardId, rev],
    );

    const refreshRunDrafts = useCallback(async () => {
        bump();
        if (!lastRun) return;
        try {
            const { data } = await request('GET', '/admin/question-bank/ai-drafts', { query: { standardId, runId: lastRun.result.run.id } });
            setLastRun((cur) => (cur ? { ...cur, drafts: data.drafts || [] } : cur));
        } catch {
            // The action itself succeeded; the Review tab reload shows the true state if this refresh fails.
        }
    }, [bump, lastRun, request, standardId]);

    const onRunComplete = useCallback(async (result) => {
        setLastRun({ result, drafts: result.drafts || [] });
        bump();
    }, [bump]);

    const pendingReview = preview.data?.summary?.aiUnapproved || 0;

    const items = [
        {
            key: 'draft',
            label: 'Draft with AI',
            children: (
                <div>
                    <AiCurateForm standardId={standardId} standardLabel={standardLabel} onRunComplete={onRunComplete} />
                    {lastRun && (
                        <div style={{ marginTop: 24 }}>
                            <AiRunResults result={lastRun.result} drafts={lastRun.drafts} standardId={standardId} onChanged={refreshRunDrafts} />
                        </div>
                    )}
                </div>
            ),
        },
        {
            key: 'review',
            label: 'Review AI drafts',
            count: pendingReview > 0 ? pendingReview : undefined,
            children: <ReviewPanel standardId={standardId} rev={rev} onChanged={refreshRunDrafts} />,
        },
        {
            key: 'questions',
            label: 'Questions',
            children: <QuestionsPanel standardId={standardId} preview={preview.data} rev={rev} onChanged={refreshRunDrafts} />,
        },
        {
            key: 'publish',
            label: 'Publish',
            children: <PublishPanel standardId={standardId} standardLabel={standardLabel} preview={preview} onPublished={refreshRunDrafts} />,
        },
        { key: 'domains', label: 'Source domains', children: <SourceDomainManager /> },
    ];

    return <PageTabs items={items} defaultActiveKey="draft" />;
};

/**
 * Question Bank Curator (platform_admin only; the page gates before mounting this, so no request is
 * made for any other persona). One standard at a time: every list, preview and publish is for it.
 */
const QuestionBankCuratorConsole = () => {
    const { request } = useApiRequest();
    const [standardId, setStandardId] = useState('iso.27001-2022');

    // A failed registry load is non-fatal: the picker falls back to the standard ids.
    const standards = useResource(async () => (await request('GET', '/standards')).data.standards || [], [request]);
    const options = useMemo(() => curatableStandardOptions(standards.data), [standards.data]);
    const label = options.find((o) => o.value === standardId)?.label || standardId;

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Text strong>Standard </Text>
                <Select
                    aria-label="Standard"
                    style={{ minWidth: 280 }}
                    value={standardId}
                    onChange={(v) => v && setStandardId(v)}
                    options={options}
                />
            </div>
            <Workspace key={standardId} standardId={standardId} standardLabel={label} />
        </div>
    );
};

export default QuestionBankCuratorConsole;
